// NEW_FILE_JUSTIFIED: Document upload form state for DocumentUploadDialog — single state object to stay under 2-useState-per-component limit
import { useState, useCallback, useRef } from 'react';

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50MB
const ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.txt', '.md', '.png', '.jpg', '.jpeg', '.webp'];

interface IUploadFormState {
  files: File[];
  isShared: boolean;
  isDragOver: boolean;
  error: string | null;
  isSubmitting: boolean;
}

const INITIAL_STATE: IUploadFormState = {
  files: [],
  isShared: false,
  isDragOver: false,
  error: null,
  isSubmitting: false,
};

function validateFile(file: File): string | null {
  const lowerName = file.name.toLowerCase();
  const hasValidExtension = ACCEPTED_EXTENSIONS.some(ext => lowerName.endsWith(ext));
  if (!hasValidExtension) {
    return `"${file.name}" is not a supported file type. Accepted: PDF, DOCX, TXT, MD, PNG, JPG, JPEG, WEBP.`;
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `"${file.name}" exceeds the 50MB size limit.`;
  }
  return null;
}

export function useDocumentUploadForm() {
  const [state, setState] = useState<IUploadFormState>(INITIAL_STATE);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = useCallback((incoming: FileList | File[]) => {
    const fileArray = Array.from(incoming);
    setState(prev => {
      const existingNames = new Set(prev.files.map(f => f.name));
      const toAdd: File[] = [];
      let error: string | null = null;

      for (const file of fileArray) {
        if (existingNames.has(file.name)) continue;
        const validationError = validateFile(file);
        if (validationError) {
          error = validationError;
          break;
        }
        toAdd.push(file);
      }

      return {
        ...prev,
        files: error ? prev.files : [...prev.files, ...toAdd],
        error,
      };
    });
  }, []);

  const removeFile = useCallback((index: number) => {
    setState(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
      error: null,
    }));
  }, []);

  const setIsDragOver = useCallback((isDragOver: boolean) => {
    setState(prev => ({ ...prev, isDragOver }));
  }, []);

  const setIsShared = useCallback((isShared: boolean) => {
    setState(prev => ({ ...prev, isShared }));
  }, []);

  const setIsSubmitting = useCallback((isSubmitting: boolean) => {
    setState(prev => ({ ...prev, isSubmitting }));
  }, []);

  const reset = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  return {
    files: state.files,
    isShared: state.isShared,
    isDragOver: state.isDragOver,
    error: state.error,
    isSubmitting: state.isSubmitting,
    fileInputRef,
    addFiles,
    removeFile,
    setIsDragOver,
    setIsShared,
    setIsSubmitting,
    reset,
  };
}
